import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { cs } from "date-fns/locale";
import type { Matcher } from "react-day-picker";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/time";
import { Calendar } from "@/ui/primitives/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/primitives/popover";
import { LGoButton } from "./LGoButton";

export interface LGoDayPickerProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "value" | "onChange"> {
  value: Date | undefined;
  onChange: (day: Date) => void;
  disabledDays?: Matcher | Matcher[];
}

/** A day is picked, never un-picked: the calendar's own toggle would empty
 *  the field when the partner clicks the day already chosen, and every form
 *  this sits in needs a day, so that click confirms the day and closes the
 *  calendar instead. */
export const LGoDayPicker = React.forwardRef<HTMLButtonElement, LGoDayPickerProps>(
  function LGoDayPicker({ value, onChange, disabledDays, className, ...props }, ref) {
    const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

    const pickTheDay = (day: Date | undefined) => {
      if (day) onChange(day);
      setIsCalendarOpen(false);
    };

    return (
      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <LGoButton
            ref={ref}
            type="button"
            variant="secondary"
            className={cn("w-full justify-between px-3 font-normal", className)}
            iconRight={<CalendarIcon className="opacity-50" />}
            {...props}
          >
            {value ? formatDate(value) : "Vyberte datum"}
          </LGoButton>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            defaultMonth={value}
            onSelect={pickTheDay}
            disabled={disabledDays}
            locale={cs}
            weekStartsOn={1}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  },
);
